/** Small geography helpers shared by the World tab and (later) Shabbat times. */

export const EARTH_RADIUS_KM = 6371;

const rad = (d: number) => (d * Math.PI) / 180;

/** Great-circle distance in kilometres (haversine). */
export function distanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLat = rad(lat2 - lat1);
  const dLng = rad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Unit vector on the sphere for a lat/lng (x toward lng 0, z toward the north pole). */
export function toVector(lat: number, lng: number): [number, number, number] {
  const la = rad(lat);
  const lo = rad(lng);
  return [Math.cos(la) * Math.cos(lo), Math.cos(la) * Math.sin(lo), Math.sin(la)];
}

/** lat/lng of the mean direction of several points (a good "centre" for framing). */
export function centroid(points: Array<{ lat: number; lng: number }>): { lat: number; lng: number } | null {
  if (!points.length) return null;
  let x = 0;
  let y = 0;
  let z = 0;
  for (const p of points) {
    const [a, b, c] = toVector(p.lat, p.lng);
    x += a;
    y += b;
    z += c;
  }
  const len = Math.hypot(x, y, z);
  if (len < 1e-9) return { lat: points[0].lat, lng: points[0].lng };
  return { lat: (Math.asin(z / len) * 180) / Math.PI, lng: (Math.atan2(y, x) * 180) / Math.PI };
}

/** Angular distance in degrees between two points (0–180). */
export function angularDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const [a, b, c] = toVector(lat1, lng1);
  const [d, e, f] = toVector(lat2, lng2);
  const dot = Math.max(-1, Math.min(1, a * d + b * e + c * f));
  return (Math.acos(dot) * 180) / Math.PI;
}

/** Normalise a longitude into (-180, 180]. */
export function wrapLng(lng: number): number {
  let l = lng;
  while (l <= -180) l += 360;
  while (l > 180) l -= 360;
  return l;
}
